import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Section Rendering for product cards hardcodes `show_sale_price_first: true`,
 * so incoming markup may disagree with the live card's Price block setting.
 * Prefer the live container's `price--sale-first` class (mirrors Liquid); fall
 * back to DOM order when both sale and compare-at groups are already present.
 *
 * @param {Element} priceContainer
 * @returns {boolean}
 */
function prefersSalePriceFirst(priceContainer) {
  if (priceContainer.classList.contains('price--sale-first')) return true;

  const groups = priceContainer.querySelectorAll(':scope > [role="group"]');
  if (groups.length >= 2) {
    return Boolean(groups[0]?.querySelector('.sale-price'));
  }

  return false;
}

/**
 * Aligns incoming compare-at/sale order with the live card's preference.
 *
 * @param {Element} currentPriceContainer
 * @param {Element} newPriceContainer
 */
function normalizePriceContentOrder(currentPriceContainer, newPriceContainer) {
  const saleFirst = prefersSalePriceFirst(currentPriceContainer);
  const groups = Array.from(newPriceContainer.querySelectorAll(':scope > [role="group"]'));
  if (groups.length < 2) return;

  const saleGroup = groups.find((group) => group.querySelector('.sale-price'));
  const compareGroup = groups.find((group) => group.querySelector('.compare-at-price'));
  if (!saleGroup || !compareGroup) return;

  const firstShowsSale = Boolean(groups[0]?.querySelector('.sale-price'));
  if (firstShowsSale === saleFirst) return;

  if (saleFirst) {
    newPriceContainer.prepend(saleGroup);
    saleGroup.insertAdjacentElement('afterend', compareGroup);
  } else {
    newPriceContainer.prepend(compareGroup);
    compareGroup.insertAdjacentElement('afterend', saleGroup);
  }
}

/**
 * Keeps the live container's class list so morph / replace cannot adopt the
 * section-rendering `price--sale-first` class when the Price block has it off.
 *
 * @param {Element} currentPriceContainer
 * @param {Element} newPriceContainer
 */
function preservePriceContainerClass(currentPriceContainer, newPriceContainer) {
  const currentClass = currentPriceContainer.getAttribute('class');
  if (currentClass == null) {
    newPriceContainer.removeAttribute('class');
  } else {
    newPriceContainer.setAttribute('class', currentClass);
  }
}

/**
 * When the number of price groups differs between the live card and the
 * incoming variant markup, the sale/not-on-sale structure changed (e.g. a
 * partial-sale card that started with a single plain price now needs a separate
 * sale + compare-at group). A keyless, childrenOnly morph reconciles these
 * structurally different subtrees positionally, which can flip the sale/compare
 * order and push the sale price into the card's side margin.
 * Promotional and plain prices both have zero price groups, so also detect
 * entering or leaving promotional pricing before choosing the morph path.
 *
 * In that case replace the container's children with the server-rendered markup
 * (reordered to match the live card's show-sale-price-first preference) and
 * keep the live container's class. The `[ref="priceContainer"]` element itself
 * is preserved so refs stay valid.
 *
 * @param {Element} currentPriceContainer
 * @param {Element} newPriceContainer
 * @returns {boolean} true when a full replacement was performed (skip morph)
 */
function replacePriceContentOnStructureChange(currentPriceContainer, newPriceContainer) {
  const currentGroupCount = currentPriceContainer.querySelectorAll(':scope > [role="group"]').length;
  const newGroupCount = newPriceContainer.querySelectorAll(':scope > [role="group"]').length;
  const currentIsPromotional = Boolean(currentPriceContainer.querySelector('.promotional-price'));
  const newIsPromotional = Boolean(newPriceContainer.querySelector('.promotional-price'));

  if (currentGroupCount === newGroupCount && currentIsPromotional === newIsPromotional) return false;

  const replacement = /** @type {Element} */ (newPriceContainer.cloneNode(true));
  normalizePriceContentOrder(currentPriceContainer, replacement);
  currentPriceContainer.replaceChildren(...Array.from(replacement.childNodes));

  return true;
}

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLAnchorElement} [cardGalleryLink] - The card gallery link element.
 * @property {HTMLAnchorElement} [productTitleLink] - The product title link element.
 * @property {HTMLElement} [productPromotion] - The optional product promotion element.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.#updateProductUrl(event);
    this.#updateCardContent(event.detail.data.html);
    this.#isUnavailableVariantSelected(event);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    if (event.detail.data.newProduct?.id) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.detail.data.newProduct?.available) {
      this.dataset.optionAvailable = event.detail.data.newProduct.available;
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    if (!overflowList || overflowList.hasAttribute('disabled')) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the product-card fragments that can change when switching swatches.
   * @param {Document | Element} newHtml
   */
  #updateCardContent(newHtml) {
    this.#morphMatchingElement(this.refs.cardGalleryLink, newHtml.querySelector('[ref="cardGalleryLink"]'));
    this.#syncBadges(newHtml);
    this.#syncOptionalElement(
      /** @type {Element | null | undefined} */ (this.refs.quickAdd),
      newHtml.querySelector('[ref="quickAdd"]'),
      {
        anchor: this.refs.cardGallery,
        position: 'beforeend',
      }
    );
    this.#syncOptionalElement(
      this.querySelector('.product-grid-view-zoom-out--details'),
      newHtml.querySelector('.product-grid-view-zoom-out--details'),
      {
        anchor: this.refs.cardGallery,
        position: 'afterend',
      }
    );
    this.#syncLinkedTextContent(this.refs.productTitleLink, newHtml.querySelector('[ref="productTitleLink"]'));
    this.#syncOptionalTextContent(
      /** @type {HTMLElement | null | undefined} */ (this.refs.productPromotion),
      /** @type {HTMLElement | null} */ (newHtml.querySelector('[ref="productPromotion"]')),
      {
        anchor: this.refs.productTitleLink,
        position: 'afterend',
      }
    );
    this.#syncPriceContent(newHtml);
    this.#syncSkuContent(newHtml);
  }

  /**
   * Morphs a live element to its updated counterpart when both exist.
   * @param {Element | undefined | null} currentElement
   * @param {Element | null} newElement
   */
  #morphMatchingElement(currentElement, newElement) {
    if (!currentElement || !newElement) return;
    morph(currentElement, newElement);
  }

  /**
   * Syncs the sold-out/sale/one-size badges wrapper for the newly selected variant.
   *
   * Update-only: never inserts a `.product-badges` wrapper onto a card surface that
   * doesn't already render one server-side (e.g. featured product cards, which never
   * show badges), and never removes one that's already there.
   *
   * `.product-badges`' own class/style (availability-driven, e.g. the sold-out
   * centering class) is availability-dependent, so `morph`'s default `childrenOnly`
   * behavior would leave it stuck on the previously selected variant. Copy the
   * wrapper's own attributes across before morphing its children.
   * @param {Document | Element} newHtml
   */
  #syncBadges(newHtml) {
    const currentBadges = this.querySelector('.product-badges');
    const newBadges = newHtml.querySelector('.product-badges');
    if (!currentBadges || !newBadges) return;

    currentBadges.className = newBadges.className;
    const newStyle = newBadges.getAttribute('style');
    if (newStyle) {
      currentBadges.setAttribute('style', newStyle);
    } else {
      currentBadges.removeAttribute('style');
    }

    morph(currentBadges, newBadges);
  }

  /**
   * Keeps the existing title markup/styles but updates href and text.
   * @param {HTMLAnchorElement | undefined | null} currentElement
   * @param {Element | null} newElement
   */
  #syncLinkedTextContent(currentElement, newElement) {
    if (!currentElement || !(newElement instanceof HTMLAnchorElement)) return;

    currentElement.href = newElement.href;

    const currentTextContainer =
      currentElement.querySelector('.text-block > *, .title-text') || currentElement.querySelector('.text-block');
    const newTextContainer = newElement.querySelector('.text-block > *, .title-text') || newElement.querySelector('.text-block');
    const nextText = newTextContainer?.textContent?.trim();

    if (currentTextContainer && nextText) {
      currentTextContainer.textContent = nextText;
    }
  }

  /**
   * Syncs optional text content that may appear or disappear between variants.
   * @param {HTMLElement | undefined | null} currentElement
   * @param {HTMLElement | null} newElement
   * @param {{ anchor?: Element | null, position?: InsertPosition }} [options]
   */
  #syncOptionalTextContent(currentElement, newElement, options = {}) {
    if (currentElement && newElement) {
      currentElement.textContent = newElement.textContent;
      return;
    }

    if (currentElement && !newElement) {
      currentElement.remove();
      return;
    }

    if (!currentElement && newElement && options.anchor) {
      options.anchor.insertAdjacentElement(options.position || 'afterend', newElement);
    }
  }

  /**
   * Updates the price markup while preserving the existing wrapper styles/classes.
   * @param {Document | Element} newHtml
   */
  #syncPriceContent(newHtml) {
    const currentPrice = this.querySelector('product-price');
    const newPrice = newHtml.querySelector('product-price');
    const currentPriceContainer = currentPrice?.querySelector('[ref="priceContainer"]');
    const newPriceContainer = newPrice?.querySelector('[ref="priceContainer"]');
    const currentNote = currentPrice?.querySelector('.volume-pricing-note');
    const newNote = newPrice?.querySelector('.volume-pricing-note');

    if (
      currentPriceContainer &&
      newPriceContainer &&
      replacePriceContentOnStructureChange(currentPriceContainer, newPriceContainer)
    ) {
      // Pricing structure changed: children replaced, live order/class kept.
    } else if (currentPriceContainer && newPriceContainer) {
      normalizePriceContentOrder(currentPriceContainer, newPriceContainer);
      preservePriceContainerClass(currentPriceContainer, newPriceContainer);
      this.#morphMatchingElement(currentPriceContainer, newPriceContainer);
    }

    this.#syncOptionalElement(currentNote || null, newNote || null, {
      anchor: currentPriceContainer || null,
      position: 'afterend',
    });
  }

  /**
   * Updates SKU text while preserving the existing wrapper styles/classes.
   * @param {Document | Element} newHtml
   */
  #syncSkuContent(newHtml) {
    const currentSku = this.querySelector('product-sku-component');
    const newSku = newHtml.querySelector('product-sku-component');

    if (!currentSku || !newSku) return;

    currentSku.setAttribute('style', newSku.getAttribute('style') || '');
    this.#morphMatchingElement(currentSku.querySelector('[ref="skuContainer"]'), newSku.querySelector('[ref="skuContainer"]'));
  }

  /**
   * Syncs an optional element that may appear or disappear between variants.
   * @param {Element | undefined | null} currentElement
   * @param {Element | null} newElement
   * @param {{ anchor?: Element | null, position?: InsertPosition }} [options]
   */
  #syncOptionalElement(currentElement, newElement, options = {}) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
      return;
    }

    if (currentElement && !newElement) {
      currentElement.remove();
      return;
    }

    if (!currentElement && newElement && options.anchor) {
      options.anchor.insertAdjacentElement(options.position || 'afterend', newElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('[ref="productCardLink"]');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.#resolveAbTestImageId(this.variantPicker?.selectedOption.dataset.optionMediaId);

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * The per-section product image A/B test swaps the first two gallery slides
   * server-side for opted-in products. That swap only reorders slides, it
   * doesn't change which media id represents the "default" image, so any
   * id-based selection (variant/swatch reset) would otherwise keep jumping
   * back to the pre-swap image. Redirect that one id to the swapped-in slide.
   * @param {string | undefined} id - The media id the caller wants to select.
   * @returns {string | undefined} The id to select, swap-adjusted.
   */
  #resolveAbTestImageId(id) {
    const { slideshow } = this.refs;
    if (!id || !slideshow) return id;

    const { abTestOriginalMediaId, abTestTargetMediaId } = slideshow.dataset;

    if (abTestOriginalMediaId && abTestTargetMediaId && id === abTestOriginalMediaId) {
      return abTestTargetMediaId;
    }

    return id;
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.#resolveAbTestImageId(this.variantPicker.selectedOption.dataset.optionMediaId);
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate);
    this.addEventListener(ThemeEvents.variantUpdate, this.#resourceCardVariantUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#resourceCardVariantUpdate);
    super.disconnectedCallback();
  }

  /**
   * Reflow PLP overflow-list after DOM updates (e.g. morph). Skips when list is disabled (e.g. show-all).
   */
  #reflowSwatchesOverflowList() {
    requestAnimationFrame(() => {
      const overflowList = this.querySelector('.plp-color-swatches__overflow-list');
      if (!overflowList || overflowList.hasAttribute('disabled')) return;
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Re-renders only the swatch content from the section-rendered product card response.
   * @param {Document | Element} newHtml
   * @returns {{ id: string, url: string, available: string } | undefined}
   */
  updateVariantPicker(newHtml) {
    /** @type {{ id: string, url: string, available: string } | undefined} */
    let newProduct;

    const currentSwatches = this.querySelector('.plp-color-swatches');
    const newSwatches = newHtml.querySelector('.plp-color-swatches');
    const currentVariantData = this.querySelector('script[type="application/json"]');
    const newVariantData = newHtml.querySelector('variant-picker script[type="application/json"]');
    const checkedInput = /** @type {HTMLInputElement | null} */ (newSwatches?.querySelector('input:checked'));
    const newProductId = checkedInput?.dataset.optionValueId;
    const newProductUrl = checkedInput?.dataset.connectedProductUrl;
    const newOptionAvailable = checkedInput?.dataset.optionAvailable;

    if (!currentSwatches || !newSwatches) {
      throw new Error('No swatch variant picker source found');
    }

    if (!currentVariantData || !newVariantData) {
      throw new Error('No swatch variant data source found');
    }

    if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
      // @ts-ignore
      newProduct = { id: newProductId, url: newProductUrl, available: newOptionAvailable };
    }

    if (newProductId) {
      this.dataset.productId = newProductId;
    }

    if (newProductUrl) {
      this.dataset.productUrl = newProductUrl;
    }

    if (newOptionAvailable) {
      this.dataset.optionAvailable = newOptionAvailable;
    }

    morph(currentSwatches, newSwatches);
    currentVariantData.textContent = newVariantData.textContent;

    this.updatedCallback();
    this.updateVariantPickerCss();
    this.#reflowSwatchesOverflowList();

    return newProduct;
  }

  /**
   * Updates the card URL when a variant is selected (product card or predictive-search resource card).
   */
  #handleCardVariantUrlUpdate = () => {
    if (!this.pendingVariantId) return;

    if (this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
      return;
    }

    const resourceCard = this.closest('.resource-card');
    const link = resourceCard?.querySelector('.resource-card__link');
    if (link instanceof HTMLAnchorElement) {
      const currentUrl = new URL(link.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      link.href = currentUrl.toString();
    }
    this.pendingVariantId = null;
  };

  /**
   * Predictive search product rows use resource-card instead of product-card; sync link, view-transition data, and price.
   * @param {Event} event
   */
  #resourceCardVariantUpdate = (event) => {
    const resourceCard = this.closest('.resource-card');
    if (!resourceCard) return;

    if (!(event instanceof VariantUpdateEvent) || !event.detail?.data?.html) return;
    const html = event.detail.data.html;
    if (!(html instanceof Document || html instanceof Element)) return;

    const anchorElement = html.querySelector('[ref="productCardLink"]');
    const resourceLink = resourceCard.querySelector('.resource-card__link');
    if (anchorElement instanceof HTMLAnchorElement && resourceLink instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute('href')?.trim() !== '') {
        resourceLink.href = anchorElement.href;
      }
    }

    const productCardLink = resourceCard.closest('product-card-link');
    const featuredFromSection = html.querySelector('product-card-link')?.getAttribute('data-featured-media-url');
    if (featuredFromSection && productCardLink) {
      productCardLink.setAttribute('data-featured-media-url', featuredFromSection);
    }

    const currentPrice = resourceCard.querySelector('product-price');
    const newPrice = html.querySelector('product-price');
    const currentPriceContainer = currentPrice?.querySelector('[ref="priceContainer"]');
    const newPriceContainer = newPrice?.querySelector('[ref="priceContainer"]');

    if (currentPriceContainer && newPriceContainer) {
      if (!replacePriceContentOnStructureChange(currentPriceContainer, newPriceContainer)) {
        normalizePriceContentOrder(currentPriceContainer, newPriceContainer);
        preservePriceContainerClass(currentPriceContainer, newPriceContainer);
        morph(currentPriceContainer, newPriceContainer);
      }
    }

    const currentNote = currentPrice?.querySelector('.volume-pricing-note');
    const newNote = newPrice?.querySelector('.volume-pricing-note');
    if (currentNote && newNote) {
      morph(currentNote, newNote);
    } else if (currentNote && !newNote) {
      currentNote.remove();
    } else if (!currentNote && newNote && currentPriceContainer) {
      currentPriceContainer.insertAdjacentElement('afterend', newNote);
    }
  };

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
